import { FC, useEffect, useState } from 'react';
import Loader from '../../components/loader/Loader';
import { useNotify } from '../../components/notifications/NotificationsProvider';
import { useWallet } from '../../components/wallet/WalletProvider';
import { removeEmployee, updateField } from '../../services/salaryActions';
import { EditableField } from '../../utils/api';
import { toInputAmount } from '../../utils/amounts';
import { validateAmount } from '../../utils/validateForm';
import { EditEmployeeModalProps } from '../../interfaces/props/EditEmployeeModalProps';
import './EditEmployeeModal.css';

const FIELDS: Array<{
  field: EditableField;
  label: string;
  button: string;
  done: string;
}> = [
  {
    field: 'salary',
    label: 'Оклад',
    button: 'Изменить оклад',
    done: 'Оклад изменён',
  },
  {
    field: 'bonus',
    label: 'Премия',
    button: 'Изменить премию',
    done: 'Премия изменена',
  },
  {
    field: 'penalty',
    label: 'Штраф',
    button: 'Изменить штраф',
    done: 'Штраф изменён',
  },
];

const EditEmployeeModal: FC<EditEmployeeModalProps> = ({
  employee,
  onClose,
  onChanged,
}) => {
  const { isReady } = useWallet();
  const notify = useNotify();
  const wallet = employee.wallet_address;
  const [values, setValues] = useState<Record<EditableField, string>>({
    salary: toInputAmount(employee.salary),
    bonus: toInputAmount(employee.bonus),
    penalty: toInputAmount(employee.penalty),
  });
  const [loading, setLoading] = useState<boolean>(false);
  // Удаление стирает и невыплаченную премию — второй клик подтверждает.
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const run = async (action: () => Promise<void>, success: string) => {
    setLoading(true);

    try {
      await action();
      notify.success(success);
      onChanged();
      onClose();
    } catch (error) {
      notify.error((error as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = (field: EditableField, label: string, done: string) => {
    const error = validateAmount(values[field], label, {
      positive: field === 'salary',
    });

    if (error) {
      notify.error(error);
      return;
    }

    run(
      () => updateField(wallet, field, values[field].trim()),
      `${done}: ${employee.full_name}`,
    );
  };

  const handleDelete = () => {
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }

    run(() => removeEmployee(wallet), `Сотрудник ${employee.full_name} удалён`);
  };

  return (
    <div className="edit-employee-modal__overlay">
      <div className="edit-employee-modal__container">
        <h2 className="edit-employee-modal__title">{employee.full_name}</h2>
        {!isReady && (
          <p className="edit-employee-modal__hint">
            Подключите кошелёк в сети BSC, чтобы менять данные
          </p>
        )}
        <form onSubmit={(e) => e.preventDefault()}>
          {FIELDS.map(({ field, label, button, done }) => (
            <div key={field} className="edit-employee-modal__input-group">
              <label>{label}:</label>
              <input
                type="text"
                inputMode="decimal"
                value={values[field]}
                onChange={(e) =>
                  setValues({ ...values, [field]: e.target.value })
                }
              />
              <button
                type="button"
                disabled={!isReady || loading}
                onClick={() => handleUpdate(field, label, done)}
              >
                {button}
              </button>
            </div>
          ))}
        </form>
        <div className="edit-employee-modal__actions">
          <button
            className="edit-employee-modal__delete-button"
            disabled={!isReady || loading}
            onClick={handleDelete}
          >
            {confirmDelete
              ? 'Точно удалить? Премия сгорит'
              : 'Удалить сотрудника'}
          </button>
          <button
            className="edit-employee-modal__close-button"
            onClick={onClose}
          >
            Закрыть
          </button>
        </div>
      </div>
      {loading && <Loader />}
    </div>
  );
};

export default EditEmployeeModal;
